import { matchRoutes, useInRouterContext, useLocation } from 'react-router-dom'
import routes from '../components/Router/routes'

export type Crumb = {
	label: string
	route?: string
	routeState?: string
}

// Auto-derives the section-level breadcrumb (e.g. "Purchases") for the current route from the
// route registry's parentId/menuName, so pages don't need to hardcode their section's label/path.
// Returns undefined when the current route (or its match) has no parentId - callers must supply
// that level of the breadcrumb explicitly in that case.
//
// Safe to call outside a Router context (e.g. component tests that render in isolation without a
// <Router>) - useInRouterContext() is itself safe to call unconditionally without a Router, and
// whether a given render tree is wrapped in a Router doesn't change across a component's lifetime,
// so bailing out before calling useLocation() here doesn't violate the rules of hooks in practice.
function useSectionCrumb(): Crumb | undefined {
	const inRouterContext = useInRouterContext()
	// eslint-disable-next-line react-hooks/rules-of-hooks -- inRouterContext is stable for a mounted component's lifetime (a component's ancestry can't gain/lose a Router without remounting), so this conditional call never changes hook order across renders.
	const location = inRouterContext ? useLocation() : undefined

	if (!inRouterContext || !location) {
		return undefined
	}

	const routeEntries = Object.values(routes)

	const matches = matchRoutes(routeEntries, location)
	const matchedRoute = matches?.[0]?.route

	if (!matchedRoute?.parentId) {
		return undefined
	}

	const parentRoute = routeEntries.find(route => route.id === matchedRoute.parentId)
	if (!parentRoute?.menuName) {
		return undefined
	}

	return { label: parentRoute.menuName, route: parentRoute.path }
}

export default useSectionCrumb

